/**
 * SbN Client Detail — sessions list, drill-down, compare, export, link.
 *
 * Route: /sbn/clients/:clientId
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  ArrowLeft, Loader2, Download, GitCompare, Link2, Link2Off,
  Activity, Calendar, FileText,
} from "lucide-react";

function fmtDT(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

// SbN result values are surfaced to the practitioner and copied into
// letters. The house style asks for a consistent 2-decimal-place
// presentation so a "95" and a "95.5000" don't sit next to each other
// in the same table.
function fmtNum2(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toFixed(2);
}

export default function SbnClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [link, setLink] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [detailSess, setDetailSess] = useState(null);   // session object opened in dialog
  const [detailLoading, setDetailLoading] = useState(false);
  const [selected, setSelected] = useState([]);         // [session_id, session_id] for compare
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareData, setCompareData] = useState(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkEmail, setLinkEmail] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get(`/sbn/clients/${encodeURIComponent(clientId)}`),
        api.get(`/sbn/clients/${encodeURIComponent(clientId)}/sessions`, {
          params: { take: 50, skip: 0 },
        }),
      ]);
      setClient(c.data?.client || null);
      setLink(c.data?.link || null);
      setSessions(s.data?.sessions || []);
      setTotal(s.data?.total_count || 0);
    } catch (e) {
      toast.error("Load failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLoading(false); }
  }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [c, s] = await Promise.all([
          api.get(`/sbn/clients/${encodeURIComponent(clientId)}`),
          api.get(`/sbn/clients/${encodeURIComponent(clientId)}/sessions`, {
            params: { take: 50, skip: 0 },
          }),
        ]);
        if (cancelled) return;
        setClient(c.data?.client || null);
        setLink(c.data?.link || null);
        setSessions(s.data?.sessions || []);
        setTotal(s.data?.total_count || 0);
      } catch (e) {
        if (!cancelled) {
          toast.error("Load failed", {
            description: e?.response?.data?.detail || e.message,
          });
        }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [clientId]);

  async function openSession(sessionId) {
    setDetailLoading(true);
    try {
      const r = await api.get(`/sbn/sessions/${encodeURIComponent(sessionId)}`);
      setDetailSess(r.data);
    } catch (e) {
      toast.error("Session load failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setDetailLoading(false); }
  }

  function toggleSelect(sessionId) {
    setSelected((cur) => {
      if (cur.includes(sessionId)) return cur.filter((x) => x !== sessionId);
      if (cur.length >= 2) return [cur[1], sessionId];  // rolling window of 2
      return [...cur, sessionId];
    });
  }

  async function runCompare() {
    if (selected.length !== 2) return;
    // Ensure chronological order: A = older, B = newer
    const sorted = [...selected].sort((x, y) => {
      const sx = sessions.find((s) => s.session_id === x);
      const sy = sessions.find((s) => s.session_id === y);
      return new Date(sx?.time || 0) - new Date(sy?.time || 0);
    });
    setCompareLoading(true);
    setCompareOpen(true);
    try {
      const r = await api.get("/sbn/compare", {
        params: { a: sorted[0], b: sorted[1] },
      });
      setCompareData(r.data);
    } catch (e) {
      toast.error("Compare failed", {
        description: e?.response?.data?.detail || e.message,
      });
      setCompareOpen(false);
    } finally { setCompareLoading(false); }
  }

  function exportCsv(sessionId) {
    const url = `${API}/sbn/sessions/${encodeURIComponent(sessionId)}/export?format=csv`;
    window.open(url, "_blank");
  }
  function exportJson(sessionId) {
    const url = `${API}/sbn/sessions/${encodeURIComponent(sessionId)}/export?format=json`;
    window.open(url, "_blank");
  }

  async function saveLink() {
    if (!linkEmail.trim() || !linkEmail.includes("@")) {
      toast.error("Enter a valid client email");
      return;
    }
    setLinkBusy(true);
    try {
      const name = [client?.first_name, client?.last_name].filter(Boolean).join(" ");
      await api.post("/sbn/links", {
        email: linkEmail.trim().toLowerCase(),
        sbn_client_id: clientId,
        sbn_client_name: name || null,
      });
      toast.success("Linked", { description: `${linkEmail.trim()} → SbN client` });
      setLinkOpen(false);
      setLinkEmail("");
      await load();
    } catch (e) {
      toast.error("Link failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally { setLinkBusy(false); }
  }

  async function removeLink() {
    if (!link?.email) return;
    if (!window.confirm(`Unlink ${link.email} from this SbN client?`)) return;
    try {
      await api.delete(`/sbn/links/by-email/${encodeURIComponent(link.email)}`);
      toast.success("Link removed");
      await load();
    } catch (e) {
      toast.error("Unlink failed", {
        description: e?.response?.data?.detail || e.message,
      });
    }
  }

  const displayName = useMemo(() => (
    [client?.first_name, client?.last_name].filter(Boolean).join(" ") || "SbN client"
  ), [client]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-500 py-10">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading SbN client…
      </div>
    );
  }
  if (!client) {
    return (
      <div className="text-sm text-zinc-600 py-10">
        SbN client not found. <button onClick={() => navigate("/sbn")} className="underline">Back to search</button>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sbn-client-detail-page">
      <button
        onClick={() => navigate("/sbn")}
        className="text-xs text-zinc-500 hover:text-zinc-900 flex items-center gap-1"
        data-testid="sbn-back-btn"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to SbN search
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Strength by Numbers · client
          </div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-950 flex items-center gap-3">
            <Activity className="h-7 w-7 text-zinc-400" />
            {displayName}
          </h1>
          <div className="mt-2 flex items-center gap-4 text-sm text-zinc-600 flex-wrap font-mono-num">
            {client.email && <span>{client.email}</span>}
            {client.date_of_birth && <span>DOB {new Date(client.date_of_birth).toLocaleDateString()}</span>}
            {client.gender && <span className="capitalize">{client.gender}</span>}
            {client.height != null && <span>{client.height} cm</span>}
            {client.weight != null && <span>{client.weight} kg</span>}
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {link ? (
              <Badge
                className="bg-emerald-100 text-emerald-800 border border-emerald-200 gap-1"
                data-testid="sbn-linked-badge"
              >
                <Link2 className="h-3 w-3" /> Linked to {link.email}
              </Badge>
            ) : (
              <Badge className="bg-zinc-100 text-zinc-700 border border-zinc-200">
                Not linked to a local client
              </Badge>
            )}
            <span className="text-xs text-zinc-500 font-mono-num">
              {total} session{total === 1 ? "" : "s"} on file
            </span>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {link ? (
            <Button
              variant="outline"
              onClick={() => navigate(`/clients/${encodeURIComponent(link.email)}`)}
              data-testid="sbn-open-local-client"
            >
              <FileText className="h-4 w-4 mr-1.5" /> Open local profile
            </Button>
          ) : null}
          {link ? (
            <Button
              variant="outline"
              onClick={removeLink}
              className="border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800"
              data-testid="sbn-unlink-btn"
            >
              <Link2Off className="h-4 w-4 mr-1.5" /> Unlink
            </Button>
          ) : (
            <Button
              onClick={() => setLinkOpen(true)}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="sbn-link-btn"
            >
              <Link2 className="h-4 w-4 mr-1.5" /> Link to local client
            </Button>
          )}
        </div>
      </div>

      {/* Compare toolbar */}
      <Card className="p-4 rounded-lg border border-zinc-200 shadow-none">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
            Compare mode
          </div>
          <div className="text-xs text-zinc-600">
            Tick two sessions to see progress metric-by-metric.
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs font-mono-num text-zinc-500">
              {selected.length} / 2 selected
            </span>
            <Button
              size="sm"
              onClick={runCompare}
              disabled={selected.length !== 2}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="sbn-compare-btn"
            >
              <GitCompare className="h-3.5 w-3.5 mr-1.5" /> Compare
            </Button>
            {selected.length > 0 && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setSelected([])}
                data-testid="sbn-compare-clear"
              >
                Clear
              </Button>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-6 rounded-lg border border-zinc-200 shadow-none">
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500 mb-3">
          Sessions
        </div>
        {sessions.length === 0 ? (
          <div className="text-sm text-zinc-500 py-6">
            No sessions on file for this client.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sbn-sessions-table">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                  <th className="py-2 pr-4 w-8"></th>
                  <th className="py-2 pr-4">Date / time</th>
                  <th className="py-2 pr-4">Tests</th>
                  <th className="py-2 pr-4">Notes</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {sessions.map((s) => {
                  const testCount = (s.tests || []).length;
                  const metricCount = (s.tests || [])
                    .reduce((acc, t) => acc + (t.results || []).length, 0);
                  return (
                    <tr
                      key={s.session_id}
                      className={cn(
                        "border-b border-zinc-100 hover:bg-zinc-50",
                        selected.includes(s.session_id) && "bg-amber-50/60 hover:bg-amber-50",
                      )}
                      data-testid={`sbn-session-row-${s.session_id}`}
                    >
                      <td className="py-3 pr-2 align-top">
                        <input
                          type="checkbox"
                          checked={selected.includes(s.session_id)}
                          onChange={() => toggleSelect(s.session_id)}
                          className="h-4 w-4 accent-zinc-900"
                          data-testid={`sbn-session-select-${s.session_id}`}
                          aria-label="Select for compare"
                        />
                      </td>
                      <td className="py-3 pr-4 align-top font-mono-num text-zinc-700">
                        <div className="flex items-center gap-1.5 text-xs">
                          <Calendar className="h-3.5 w-3.5 text-zinc-400" />
                          {fmtDT(s.time)}
                        </div>
                      </td>
                      <td className="py-3 pr-4 align-top text-xs">
                        <span className="font-mono-num text-zinc-700">
                          {testCount} test{testCount === 1 ? "" : "s"}
                        </span>
                        <span className="text-zinc-400"> · </span>
                        <span className="font-mono-num text-zinc-500">
                          {metricCount} metric{metricCount === 1 ? "" : "s"}
                        </span>
                      </td>
                      <td className="py-3 pr-4 align-top text-xs text-zinc-600 max-w-xs">
                        <div className="line-clamp-2">{s.notes || "—"}</div>
                      </td>
                      <td className="py-3 pr-4 align-top">
                        <div className="flex gap-1.5 flex-wrap justify-end">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => openSession(s.session_id)}
                            data-testid={`sbn-session-open-${s.session_id}`}
                          >
                            View
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            onClick={() => exportCsv(s.session_id)}
                            title="Download CSV"
                            data-testid={`sbn-session-csv-${s.session_id}`}
                          >
                            <Download className="h-3.5 w-3.5 mr-1" /> CSV
                          </Button>
                          <Button
                            size="sm" variant="outline"
                            onClick={() => exportJson(s.session_id)}
                            title="Download JSON"
                            data-testid={`sbn-session-json-${s.session_id}`}
                          >
                            <Download className="h-3.5 w-3.5 mr-1" /> JSON
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Session detail dialog */}
      <Dialog open={detailSess !== null || detailLoading} onOpenChange={(v) => !v && setDetailSess(null)}>
        <DialogContent
          className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="sbn-session-detail-dialog"
        >
          <DialogHeader>
            <DialogTitle>Session detail</DialogTitle>
          </DialogHeader>
          {detailLoading && !detailSess && (
            <div className="flex items-center gap-2 text-sm text-zinc-500 py-8">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading session…
            </div>
          )}
          {detailSess && (
            <div className="space-y-4">
              <div className="text-sm text-zinc-600 font-mono-num flex items-center gap-3 flex-wrap">
                <span>{fmtDT(detailSess.time)}</span>
                <span className="text-zinc-400">·</span>
                <span>{detailSess.session_id}</span>
              </div>
              {detailSess.notes && (
                <div className="text-sm text-zinc-700 bg-zinc-50 rounded p-3 border border-zinc-200">
                  {detailSess.notes}
                </div>
              )}
              {(detailSess.tests || []).length === 0 && (
                <div className="text-sm text-zinc-500 py-4">
                  No test results recorded for this session.
                </div>
              )}
              {(detailSess.tests || []).map((t, i) => (
                <div key={i} className="border border-zinc-200 rounded-md">
                  <div className="px-3 py-2 border-b border-zinc-100 bg-zinc-50 flex items-center gap-2">
                    <div className="font-semibold text-zinc-900 text-sm">{t.test}</div>
                    {t.category && (
                      <Badge className="text-2xs uppercase tracking-wider bg-zinc-200 text-zinc-700 border-0">
                        {t.category}
                      </Badge>
                    )}
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-2xs uppercase tracking-wider text-zinc-500 border-b border-zinc-100">
                        <th className="px-3 py-1.5">Metric</th>
                        <th className="px-3 py-1.5 text-right">Left</th>
                        <th className="px-3 py-1.5 text-right">Right</th>
                        <th className="px-3 py-1.5 text-right">Single</th>
                        <th className="px-3 py-1.5 text-right">Δ</th>
                        <th className="px-3 py-1.5">Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(t.results || []).map((r, j) => (
                        <tr key={j} className="border-b border-zinc-100 last:border-b-0">
                          <td className="px-3 py-1.5 font-medium text-zinc-800">{r.metric}</td>
                          <td className="px-3 py-1.5 text-right font-mono-num">{fmtNum2(r.left)}</td>
                          <td className="px-3 py-1.5 text-right font-mono-num">{fmtNum2(r.right)}</td>
                          <td className="px-3 py-1.5 text-right font-mono-num">{fmtNum2(r.single)}</td>
                          <td className="px-3 py-1.5 text-right font-mono-num">{fmtNum2(r.difference)}</td>
                          <td className="px-3 py-1.5 text-zinc-500">{r.unit || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            {detailSess && (
              <>
                <Button
                  variant="outline"
                  onClick={() => exportCsv(detailSess.session_id)}
                  data-testid="sbn-detail-csv-btn"
                >
                  <Download className="h-4 w-4 mr-1.5" /> CSV
                </Button>
                <Button
                  variant="outline"
                  onClick={() => exportJson(detailSess.session_id)}
                  data-testid="sbn-detail-json-btn"
                >
                  <Download className="h-4 w-4 mr-1.5" /> JSON
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setDetailSess(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare dialog */}
      <Dialog open={compareOpen} onOpenChange={(v) => { if (!v) { setCompareOpen(false); setCompareData(null); } }}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto" data-testid="sbn-compare-dialog">
          <DialogHeader>
            <DialogTitle>Session comparison</DialogTitle>
          </DialogHeader>
          {compareLoading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500 py-8">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading comparison…
            </div>
          )}
          {compareData && <CompareTable data={compareData} />}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCompareOpen(false); setCompareData(null); }}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link dialog */}
      <Dialog open={linkOpen} onOpenChange={(v) => !v && setLinkOpen(false)}>
        <DialogContent className="max-w-md" data-testid="sbn-link-dialog">
          <DialogHeader>
            <DialogTitle>Link SbN client to local client</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-zinc-600">
              Enter the Acuity / local client&apos;s email. Once linked, SOAP
              notes will auto-pull this SbN client&apos;s latest session data,
              and the client&apos;s profile page will show a Strength by
              Numbers tab.
            </p>
            <div>
              <Label className="text-xs">Client email</Label>
              <Input
                type="email"
                value={linkEmail}
                onChange={(e) => setLinkEmail(e.target.value)}
                placeholder={client.email || "client@example.com"}
                className="mt-1 font-mono-num"
                data-testid="sbn-link-email-input"
                autoFocus
              />
              {client.email && (
                <button
                  type="button"
                  onClick={() => setLinkEmail(client.email)}
                  className="mt-1 text-2xs text-zinc-500 hover:text-zinc-900 underline"
                  data-testid="sbn-link-use-sbn-email"
                >
                  Use SbN email: {client.email}
                </button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkOpen(false)}>Cancel</Button>
            <Button
              onClick={saveLink}
              disabled={linkBusy}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="sbn-link-save-btn"
            >
              {linkBusy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Link2 className="h-4 w-4 mr-1.5" />}
              Save link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function CompareTable({ data }) {
  const { session_a, session_b, rows } = data;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div className="border border-zinc-200 rounded-md p-3">
          <div className="text-2xs uppercase tracking-wider text-zinc-500">Session A · baseline</div>
          <div className="mt-1 font-mono-num text-zinc-800">{fmt(session_a?.time)}</div>
          <div className="text-2xs text-zinc-500 font-mono-num">{session_a?.session_id}</div>
        </div>
        <div className="border border-zinc-200 rounded-md p-3">
          <div className="text-2xs uppercase tracking-wider text-zinc-500">Session B · follow-up</div>
          <div className="mt-1 font-mono-num text-zinc-800">{fmt(session_b?.time)}</div>
          <div className="text-2xs text-zinc-500 font-mono-num">{session_b?.session_id}</div>
        </div>
      </div>

      <CompareBarChart rows={rows || []} />

      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="sbn-compare-table">
          <thead>
            <tr className="text-left text-2xs uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
              <th className="py-2 pr-3">Test · Metric</th>
              <th className="py-2 pr-3">Unit</th>
              <th className="py-2 pr-3 text-right">A · L / R / S</th>
              <th className="py-2 pr-3 text-right">B · L / R / S</th>
              <th className="py-2 pr-3 text-right">Δ (L / R / S)</th>
              <th className="py-2 pr-3 text-right">% (L / R / S)</th>
            </tr>
          </thead>
          <tbody>
            {(rows || []).map((r, i) => (
              <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                <td className="py-1.5 pr-3">
                  <div className="font-medium text-zinc-800">{r.metric}</div>
                  <div className="text-2xs text-zinc-500">{r.test}</div>
                </td>
                <td className="py-1.5 pr-3 text-zinc-500">{r.unit || ""}</td>
                <td className="py-1.5 pr-3 text-right font-mono-num text-zinc-700">
                  {trio(r.a)}
                </td>
                <td className="py-1.5 pr-3 text-right font-mono-num text-zinc-700">
                  {trio(r.b)}
                </td>
                <td className="py-1.5 pr-3 text-right font-mono-num">
                  <DeltaTrio l={r.delta_left} r={r.delta_right} s={r.delta_single} />
                </td>
                <td className="py-1.5 pr-3 text-right font-mono-num">
                  <PctTrio l={r.pct_left} r={r.pct_right} s={r.pct_single} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function fmt(v) { if (!v) return "—"; try { return new Date(v).toLocaleString(); } catch { return String(v); } }
function trio(x) {
  if (!x) return "—";
  const L = fmtNum2(x.left), R = fmtNum2(x.right), S = fmtNum2(x.single);
  return `${L} / ${R} / ${S}`;
}
function tone(v) {
  if (v == null || v === 0) return "text-zinc-500";
  return v > 0 ? "text-emerald-700" : "text-red-700";
}
function DeltaTrio({ l, r, s }) {
  return (
    <span>
      <span className={tone(l)}>{fmtNum2(l)}</span>
      <span className="text-zinc-300"> / </span>
      <span className={tone(r)}>{fmtNum2(r)}</span>
      <span className="text-zinc-300"> / </span>
      <span className={tone(s)}>{fmtNum2(s)}</span>
    </span>
  );
}
function PctTrio({ l, r, s }) {
  const fmt2 = (v) => v == null ? "—" : `${v > 0 ? "+" : ""}${v}%`;
  return (
    <span>
      <span className={tone(l)}>{fmt2(l)}</span>
      <span className="text-zinc-300"> / </span>
      <span className={tone(r)}>{fmt2(r)}</span>
      <span className="text-zinc-300"> / </span>
      <span className={tone(s)}>{fmt2(s)}</span>
    </span>
  );
}



/**
 * CompareBarChart — small horizontal bar chart of the top-N metrics
 * ranked by absolute % change. Green bars = improvement, red = decline.
 *
 * We pick a single "primary" pct per row (single > averaged L/R) so the
 * chart stays legible even when the table has 20+ rows.
 */
function CompareBarChart({ rows }) {
  const points = [];
  for (const r of rows) {
    // Prefer the single-side metric; else average the two sides that have data.
    let pct = null;
    let deltaSummary = null;
    if (r.pct_single != null) {
      pct = r.pct_single;
      deltaSummary = r.delta_single;
    } else {
      const sides = [r.pct_left, r.pct_right].filter((v) => v != null);
      if (sides.length) {
        pct = sides.reduce((s, v) => s + v, 0) / sides.length;
        pct = Math.round(pct * 100) / 100;
        const dSides = [r.delta_left, r.delta_right].filter((v) => v != null);
        if (dSides.length) {
          deltaSummary = Math.round(
            (dSides.reduce((s, v) => s + v, 0) / dSides.length) * 1000,
          ) / 1000;
        }
      }
    }
    if (pct == null) continue;
    points.push({
      label: r.metric || r.test,
      test: r.test,
      unit: r.unit || "",
      pct,
      delta: deltaSummary,
    });
  }
  if (!points.length) {
    return (
      <div
        className="border border-dashed border-zinc-200 rounded-md p-3 text-xs text-zinc-500"
        data-testid="sbn-compare-chart-empty"
      >
        No numeric % changes to chart — both sessions likely have different
        test panels, so metrics could not be paired.
      </div>
    );
  }
  points.sort((a, b) => Math.abs(b.pct) - Math.abs(a.pct));
  const top = points.slice(0, 10);
  const scale = Math.max(...top.map((p) => Math.abs(p.pct)), 1);   // never 0 → NaN

  return (
    <div
      className="border border-zinc-200 rounded-md p-4 bg-white"
      data-testid="sbn-compare-chart"
    >
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-2xs uppercase tracking-[0.2em] text-zinc-500 font-semibold">
          Top movers · % change B vs A
        </div>
        <div className="text-2xs text-zinc-500">
          <span className="inline-block h-2 w-2 rounded-sm bg-emerald-600 mr-1 align-middle" />
          gain
          <span className="inline-block h-2 w-2 rounded-sm bg-red-600 ml-3 mr-1 align-middle" />
          loss
        </div>
      </div>
      <div className="space-y-1.5">
        {top.map((p, i) => {
          const width = Math.round((Math.abs(p.pct) / scale) * 50); // 50% max
          const positive = p.pct >= 0;
          return (
            <div key={i} className="flex items-center gap-2 text-xs" data-testid={`sbn-compare-bar-${i}`}>
              <div className="w-56 truncate text-zinc-700" title={`${p.test} · ${p.label}`}>
                <span className="text-zinc-950 font-medium">{p.label}</span>
                <span className="text-zinc-400"> · {p.test}</span>
              </div>
              <div className="flex-1 flex items-center h-4">
                {/* Left half (declines) */}
                <div className="flex-1 flex justify-end pr-0.5 h-full">
                  {!positive && (
                    <div
                      className="h-full bg-red-600/85 rounded-l-sm"
                      style={{ width: `${width}%` }}
                    />
                  )}
                </div>
                {/* Center divider */}
                <div className="w-px h-full bg-zinc-300" />
                {/* Right half (gains) */}
                <div className="flex-1 pl-0.5 h-full">
                  {positive && (
                    <div
                      className="h-full bg-emerald-600/85 rounded-r-sm"
                      style={{ width: `${width}%` }}
                    />
                  )}
                </div>
              </div>
              <div
                className={cn(
                  "w-20 text-right font-mono-num tabular-nums",
                  positive ? "text-emerald-700" : "text-red-700",
                )}
              >
                {positive ? "+" : ""}{p.pct}%
              </div>
              <div className="w-14 text-right font-mono-num text-2xs text-zinc-500">
                {p.delta != null ? `${p.delta > 0 ? "+" : ""}${p.delta}${p.unit}` : ""}
              </div>
            </div>
          );
        })}
      </div>
      {points.length > top.length && (
        <div className="mt-2 text-2xs text-zinc-500">
          {points.length - top.length} more metric{points.length - top.length === 1 ? "" : "s"} in the full table below.
        </div>
      )}
    </div>
  );
}
